/**
 * `EmailMessageView` — a single message, read-only. Header (from/to/cc/date) +
 * plain-text body + attachment chips. See the C3 handoff.
 *
 * SECURITY: renders `bodyText` ONLY. `bodyHtml` is deliberately NOT rendered —
 * injecting untrusted email HTML is an XSS vector; a sanitizer + isolated
 * rendering is a follow-up. Attachments are download-only chips served by
 * `mail.read_attachment` (18 MB cap) — no inline preview, no `channels.*` media
 * path (that one is conversation-scoped and cannot serve email attachments).
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Download, Loader2, Paperclip } from "lucide-react";

import { useMailMessage } from "@/hooks/useMail";
import { readMailAttachment } from "@/api/lx-mail";
import { decodeMimeWords, stripLeadingHeaderBlock } from "@/lib/mime-decode";
import { MailWaitingCue } from "@/components/agent/MailWaitingCue";

export function EmailMessageView({
  messageId,
  subjectHint,
  onBack,
  showBack = true,
}: {
  messageId: string;
  /** The envelope subject, shown while the full message loads. */
  subjectHint?: string;
  onBack: () => void;
  /** Hidden when a persistent viewer column sits beside the list (wide layout),
   *  where "back" is redundant chrome — the list never went away. */
  showBack?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { message, isLoading, error } = useMailMessage(messageId);
  const fmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }),
    [i18n.language],
  );

  // Defensive normalisation while the runtime still returns raw RFC 2047
  // encoded-words + a header-prefixed body (handoff §2/§3). No-ops once fixed.
  const subject = decodeMimeWords(
    message?.subject || subjectHint || t("agent.channels.email.noSubject"),
  );
  const bodyText = stripLeadingHeaderBlock(message?.bodyText ?? "");

  return (
    <div
      data-testid="email-message-view"
      className="flex min-h-0 flex-1 flex-col bg-cream-50/70 dark:bg-coffee-950/50"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-cream-200/60 px-2 py-1.5 dark:border-coffee-800/60">
        {showBack && (
          <button
            type="button"
            data-testid="email-message-back"
            onClick={onBack}
            aria-label={t("agent.channels.email.back")}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-caption text-coffee-600 transition-colors hover:bg-cream-100 hover:text-coffee-900 dark:text-cream-400 dark:hover:bg-coffee-800 dark:hover:text-cream-100"
          >
            <ArrowLeft size={14} />
            {t("agent.channels.email.back")}
          </button>
        )}
        <span className="min-w-0 flex-1 truncate text-body-sm font-medium text-coffee-800 dark:text-cream-200">
          {subject}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {isLoading && !message && (
          // `mail.get` costs several IMAP sessions (resolve + read + export +
          // attachments), so this wait is long enough to need an explanation
          // rather than a spinner.
          <div className="flex items-center justify-center py-16">
            <MailWaitingCue />
          </div>
        )}

        {error && !message && (
          <div
            data-testid="email-message-error"
            role="alert"
            className="rounded border border-red-200 bg-red-50 p-3 text-body-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {t("agent.channels.loadFailed", { message: error })}
          </div>
        )}

        {message && (
          <>
            <dl className="mb-2 space-y-px border-b border-cream-200/60 pb-2 text-[11px] leading-tight dark:border-coffee-800/60">
              <Row label={t("agent.channels.email.from")} value={decodeMimeWords(message.from)} />
              {message.to && (
                <Row label={t("agent.channels.email.to")} value={decodeMimeWords(message.to)} />
              )}
              {message.cc && (
                <Row label={t("agent.channels.email.cc")} value={decodeMimeWords(message.cc)} />
              )}
              <Row label="" value={fmt.format(message.date)} muted />
            </dl>

            <div className="whitespace-pre-wrap break-words text-caption leading-relaxed text-coffee-800 dark:text-cream-200">
              {bodyText}
            </div>

            {message.attachments.length > 0 && (
              <div className="mt-4 border-t border-cream-200/60 pt-3 dark:border-coffee-800/60">
                <div className="flex flex-col gap-1">
                  {message.attachments.map((a) => (
                    <EmailAttachmentChip key={a.name} messageId={messageId} name={a.name} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** One attachment chip. Click fetches the bytes (`mail.read_attachment`, 18 MB
 *  cap) and triggers a browser download — read-only, no inline preview. */
function EmailAttachmentChip({ messageId, name }: { messageId: string; name: string }) {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");

  const download = async () => {
    if (state === "loading") return;
    setState("loading");
    try {
      const res = await readMailAttachment(messageId, name);
      const a = document.createElement("a");
      a.href = `data:${res.mime ?? "application/octet-stream"};base64,${res.bytesBase64}`;
      a.download = res.name || name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setState("idle");
    } catch {
      setState("error");
    }
  };

  return (
    <button
      type="button"
      data-testid="email-attachment"
      data-attachment-name={name}
      onClick={download}
      disabled={state === "loading"}
      title={name}
      aria-label={t("agent.channels.email.downloadAttachment", { name })}
      className="inline-flex w-fit items-center gap-1.5 rounded border border-cream-300 bg-cream-100/40 px-2 py-1 text-caption text-coffee-700 transition-colors hover:bg-cream-100 disabled:opacity-50 dark:border-coffee-700 dark:bg-coffee-900/30 dark:text-cream-300 dark:hover:bg-coffee-800"
    >
      <Paperclip size={12} className="shrink-0" aria-hidden />
      <span className="max-w-[240px] truncate">{name}</span>
      {state === "loading" ? (
        <Loader2 size={12} className="shrink-0 animate-spin" aria-hidden />
      ) : (
        <Download
          size={12}
          className={
            "shrink-0 " + (state === "error" ? "text-red-500" : "text-gold-700 dark:text-gold-400")
          }
          aria-hidden
        />
      )}
    </button>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex gap-1.5">
      {label && (
        <dt className="shrink-0 font-semibold uppercase tracking-wide text-cream-500">
          {label}
        </dt>
      )}
      <dd className={muted ? "text-cream-500" : "text-coffee-700 dark:text-cream-300"}>
        {value}
      </dd>
    </div>
  );
}
